\
<template>
    <Card title="On Hold Queue">
        <!-- Fixed thirds rather than auto-sized columns: the filter labels are nowrap, so an
             auto-sized column grows to fit the longest selection and squeezes its neighbours. -->
        <b-form-row class="mb-2">
            <b-col cols="12" md="4">
                <SearchComponent label="Store" v-model="store" searchURL="/Store" prepopulate clearable />
            </b-col>
            <b-col cols="12" md="4">
                <QueueMultiSelectFilter v-model="selectedProgramIds" label="Program"
                    :options="programFilterOptions" all-label="All Programs"
                    search-placeholder="Search programs..." :empty-text="programFilterEmptyText" />
            </b-col>
            <b-col cols="12" md="4">
                <QueueMultiSelectFilter :value="selectedDrugKeys" @input="onDrugSelectionChanged"
                    label="Drug" :options="drugFilterOptions" :counts="drugCounts"
                    all-label="All Drugs" search-placeholder="Search drugs..."
                    :empty-text="drugFilterEmptyText"
                    :options-are-complete="false" />
            </b-col>
        </b-form-row>
        <b-form-row class="mb-2">
            <b-col cols="12" md="4">
                <label>
                    Substatus:
                </label>
                <v-select
                    :value="{ value: subStatus, label: onHoldSubStatuses.find(ss => ss.value == subStatus)?.label }"
                    :options="activeOnHoldSubStatuses" @input="subStatus = $event?.value" />
            </b-col>
            <b-col cols="12" md="4">
                <DateRangeFilter v-model="dateFilter" label="Date Range" :clearable="false" />
            </b-col>
        </b-form-row>
        <b-row>
            <b-col>
                <b-table id="on-hold-table" ref="onHoldTable" api-url="Prescription/OnHold" :items="onHoldTableProvider"
                    :fields="visibleFields" :filter="tableFilter" responsive="xl" sort-icon-left show-empty
                    :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" no-local-sorting @row-clicked="selectRx">
                    <template #cell(isUrgent)="data">
                        <b-badge v-if="data.item.isUrgent" variant="danger" class="urgent-highlight">
                            URGENT
                        </b-badge>
                    </template>
                    <template #cell(actions)="data">
                        <div class="table-icon-actions">
                            <router-link :to="getPrescriptionDetailsRoute(data.item)" class="mr-1">
                                <b-icon icon="pencil-square" v-b-tooltip.hover title="Edit" />
                            </router-link>
                        </div>
                    </template>
                    <template #cell(rxId)="data">
                        <span v-b-tooltip.hover :title="`Memo: ${data.item.memo ?? ''}`">
                            {{ data.item.rxId }}
                        </span>
                    </template>
                    <template #cell(primaryPlan)="data">
                        <template v-if="data.item.primaryPlan && data.item.primaryPlan.planName">
                            <div>{{ data.item.primaryPlan.planName }}</div>
                            <small v-if="data.item.primaryPlan.pbmName" class="text-muted">
                                {{ data.item.primaryPlan.pbmName }}
                            </small>
                        </template>
                        <span v-else>-</span>
                    </template>
                </b-table>

                <BottomPagerBar v-model="pageNumber" :per-page="pageSize" :loading="loading"
                    :total-rows="totalItemCount" show-page-size-selector show-total item-name="prescription"
                    @change-page-size="pageSize = $event">
                    <template #left-after>
                        <b-button variant="info" size="sm" class="m-0 customize-columns"
                            @click="$bvModal.show('onhold-column-customize-modal')">
                            <i class="fa fa-cog"></i> Customize Columns
                        </b-button>
                    </template>
                </BottomPagerBar>
            </b-col>
        </b-row>

        <!-- Column Customize Modal -->
        <b-modal id="onhold-column-customize-modal" title="Customize Table Columns" size="lg" @ok="saveColumnPreferences">
            <div class="mb-3">
                <h6>Select which columns to display in the on-hold queue table:</h6>
            </div>

            <div class="grid">
                <h6 style="grid-column: 1 / -1;" class="mt-3 mb-2">Core Columns</h6>
                <b-form-checkbox v-model="columnVisibility.isUrgent">Urgent</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.productNameShort">Drug</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.patient">Patient</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.storeName">Store</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.rxId">Rx Id</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.quantity">Quantity</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.subStatus">Hold Status</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.nextTouchDate">Next Touch Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.fillDate">Fill Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.programName">Program</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.prescriberName">Doctor</b-form-checkbox>

                <h6 style="grid-column: 1 / -1;" class="mt-3 mb-2">Additional Columns</h6>
                <b-form-checkbox v-model="columnVisibility.ndc11">NDC</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.daySupply">Day Supply</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.memo">Memo</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.directions">Directions</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.writtenDate">Written Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.patientPay">Patient Pay</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.dispensed">Dispensed</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.primaryPlan">Primary Plan</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.lastShippedDate">Last Shipped</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.updated">Updated</b-form-checkbox>
            </div>

            <template #modal-footer="{ ok, cancel }">
                <b-button variant="secondary" @click="cancel()">Cancel</b-button>
                <b-button variant="primary" @click="ok()">Save Preferences</b-button>
            </template>
        </b-modal>
    </Card>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import SearchComponent from "@/components/SearchComponent.vue";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import Axios, { AxiosRequestConfig } from "axios";
import { Prescription } from "@/models/Prescription";
import EnumSelector from '@/components/EnumSelector.vue';
import { BvTableCtxObject } from "bootstrap-vue";
import { Patient } from "@/models/Patient";
import moment from "moment";
import { DatePicker } from "element-ui";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import TextUtils from "@/util/TextUtils";
import { getUserSetting, getUserSettingParsed, saveUserSetting } from "@/services/UserSettingsService";
import QueueMultiSelectFilter, { QueueFilterOption } from "@/components/Queue/QueueMultiSelectFilter.vue";

/**
 * Default visibility for every column, used both for a fresh page and as the base that saved
 * preferences are layered over. Both paths must read this same map: a column absent from the
 * defaults resolves to undefined, and visibleFields treats undefined as visible, so a column
 * declared hidden-by-default would otherwise reappear for anyone holding older saved preferences.
 */
const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
    // Core columns (visible by default)
    isUrgent: true,
    productNameShort: true,
    patient: true,
    storeName: true,
    rxId: true,
    quantity: true,
    subStatus: true,
    nextTouchDate: true,
    fillDate: true,
    programName: true,
    prescriberName: true,
    // Additional columns (hidden by default)
    ndc11: false,
    daySupply: false,
    memo: false,
    directions: false,
    writtenDate: false,
    patientPay: false,
    dispensed: false,
    primaryPlan: false,
    lastShippedDate: false,
    updated: false,
};

/** One drug present in the queue, as returned by the drug-counts endpoint. */
interface QueueDrug {
    drugSource: number;
    drugId: number;
    drugName: string;
    count: number;
}

/**
 * A drug the user has selected. The name is carried alongside the key so a restored selection
 * renders a label before the counts endpoint responds.
 */
interface SelectedDrug {
    key: string;
    name: string;
}

/** Drug identity is composite -- the same numeric id means different drugs across sources. */
const drugKeyOf = (drugSource: number, drugId: number): string => `${drugSource}:${drugId}`;

@Component({
    components: {
        SearchComponent,
        EnumSelector,
        [DatePicker.name]: DatePicker,
        BottomPagerBar,
        DateRangeFilter,
        QueueMultiSelectFilter
    }
})
export default class PrescriptionOnHoldQueue extends Vue {
    private store: Store = new Store();
    private subStatus: number | null = null;

    private allPrograms: Program[] = [];
    private selectedProgramIds: number[] = [];
    private programsFailed = false;

    private queueDrugs: QueueDrug[] = [];
    private selectedDrugs: SelectedDrug[] = [];
    /** Guards against a slow drug-counts response overwriting a newer one. */
    private drugCountsRequestId = 0;
    /**
     * An empty drug list means "nothing is on hold" only once a request has actually succeeded.
     * Without these the dropdown reports that same emptiness while the first request is still in
     * flight, and again when one fails -- stating as fact something the page does not yet know.
     */
    private drugCountsLoading = false;
    private drugCountsFailed = false;

    private dateFilter: { startDate: any, endDate: any } = {
        startDate: moment().subtract(14, "days").toDate(),
        endDate: new Date()
    };

    private pageNumber = 1;
    private pageSize = 100;
    private loading = true;
    private totalItemCount = 200;

    private sortBy: string = "fillDate";
    private sortDesc: boolean = false;

    private columnVisibility: any = { ...DEFAULT_COLUMN_VISIBILITY };

    /**
     * Suppresses the filter watchers while a filter is being applied by the page rather than chosen by
     * the user -- restoreFilters() replaying saved state, and setupFromQuery()/hydrateStoreName()
     * applying a deep link. Without it those assignments would persist right back over what was saved.
     */
    private restoringFilters = false;

    mounted() {
        this.pageSize = getPageSizePreference(this, 'pageSize_OnHoldQueue', 100);
        this.restoreFilters();
        this.setupFromQuery();
        this.loadColumnPreferences();
        this.getOnHoldSubStatuses();
        this.loadPrograms();
        this.loadDrugCounts();
    }

    get programFilterOptions(): QueueFilterOption[] {
        return this.allPrograms.map(p => ({ key: p.id, name: p.name }));
    }

    get selectedDrugKeys(): string[] {
        return this.selectedDrugs.map(d => d.key);
    }

    /**
     * The drugs currently in the queue, plus any already-selected drug that has dropped out of
     * scope. Without that union a selection made under one set of filters would become invisible
     * -- and therefore impossible to clear -- as soon as another filter changed.
     */
    get drugFilterOptions(): QueueFilterOption[] {
        const options: QueueFilterOption[] = this.queueDrugs.map(d => ({
            key: drugKeyOf(d.drugSource, d.drugId),
            name: d.drugName || `Unknown drug (${d.drugId})`,
        }));
        const present = new Set(options.map(o => o.key));
        return [...options, ...this.selectedDrugs.filter(d => !present.has(d.key))];
    }

    get drugFilterEmptyText(): string {
        if (this.drugCountsLoading) return "Loading drugs...";
        if (this.drugCountsFailed) return "Could not load drugs -- change a filter to try again";
        return "No drugs on hold for these filters";
    }

    get programFilterEmptyText(): string {
        return this.programsFailed ? "Could not load programs" : "No programs match";
    }

    get drugCounts(): Record<string, number> {
        const counts: Record<string, number> = {};
        for (const drug of this.queueDrugs) {
            counts[drugKeyOf(drug.drugSource, drug.drugId)] = drug.count;
        }
        return counts;
    }

    onDrugSelectionChanged(keys: Array<string | number>) {
        const byKey = new Map(this.drugFilterOptions.map(o => [o.key, o.name]));
        this.selectedDrugs = keys.map(key => ({
            key: String(key),
            name: byKey.get(key) ?? String(key),
        }));
    }

    async loadPrograms() {
        try {
            const resp = await Axios.get<any[]>("Program", { params: { active: true } });
            this.allPrograms = resp.data.map(p => new Program(p));
            this.programsFailed = false;
            this.pruneInactiveProgramSelection();
        } catch (err) {
            console.warn("Error loading programs", err);
            this.programsFailed = true;
            // The filter silently offering nothing is indistinguishable from a client with no programs,
            // so say so -- the queue is unfiltered by program either way, which is not what was asked for.
            this.$notification(NotificationOptions.error("Failed to load programs"));
        }
    }

    /**
     * Drops restored program ids that are no longer active. Only the active list is fetched, so a
     * program deactivated since it was saved has no option to render: the dropdown falls back to
     * "N selected" and the queue stays narrowed by a filter the user can neither name nor clear.
     * Widening is the safe direction to fail for a work queue -- it shows more work, never hides it.
     */
    private pruneInactiveProgramSelection() {
        // An empty list is far likelier to be a degenerate response than a client with no active
        // programs at all, and acting on it would clear every selection the user had.
        if (!this.selectedProgramIds.length || !this.allPrograms.length) return;

        const active = new Set(this.allPrograms.map(p => p.id));
        const kept = this.selectedProgramIds.filter(id => active.has(id));
        if (kept.length === this.selectedProgramIds.length) return;

        // Suppressed so the pruned list is not written back: the saved id is left alone in case the
        // program is reactivated later, and a bad /Program response cannot destroy a saved filter.
        this.restoringFilters = true;
        this.selectedProgramIds = kept;
        this.$nextTick(() => {
            this.restoringFilters = false;
            // The suppression above also swallowed the drugScope watcher, and the drug counts loaded
            // by mounted() are still scoped to the program that was just dropped.
            this.loadDrugCounts();
        });
    }

    /**
     * Loads the drugs available to filter on. Derived from tableFilter so the option list is
     * scoped exactly like the grid, minus the drug selection itself -- that omission is what keeps
     * the list stable while the user is picking, and paging/sorting are irrelevant to a count.
     */
    async loadDrugCounts() {
        const { pageNumber, pageSize, sortBy, sortDirection, drugs, ...params } = this.tableFilter;
        const requestId = ++this.drugCountsRequestId;
        this.drugCountsLoading = true;

        try {
            const resp = await Axios.get<QueueDrug[]>("Prescription/OnHold/drug-counts", { params });
            // A broader scope can take far longer than a narrow one; ignore anything overtaken.
            if (requestId !== this.drugCountsRequestId) return;
            this.queueDrugs = resp.data ?? [];
            this.drugCountsFailed = false;
        } catch (err) {
            console.warn("Error loading on hold drug counts", err);
            if (requestId !== this.drugCountsRequestId) return;
            // Counts that were true for an older scope are no more accurate than none at all, and
            // leaving them up would keep presenting them as current.
            this.queueDrugs = [];
            this.drugCountsFailed = true;
        } finally {
            if (requestId === this.drugCountsRequestId) this.drugCountsLoading = false;
        }
    }

    // The dashboard's On Hold Queue card links each cell here pre-filtered. Applied after restoreFilters
    // so an incoming link wins over the page's saved filters, and watched so a second link followed while
    // already on the page takes effect too. Arriving with no query leaves the saved filters alone.
    private setupFromQuery() {
        const query = this.$route.query;
        const hasSubStatus = "subStatus" in query;
        if (!query.storeId && !hasSubStatus) return;

        // A link is a transient view rather than a change to the filters the user chose to keep, so it
        // does not persist. restoreFilters suppresses the same way, but only when it found something to
        // restore — without this, following a link would overwrite the saved filters of exactly those
        // users who had none.
        this.restoringFilters = true;

        if (query.storeId) {
            const storeId = Number(query.storeId);
            if (isNaN(storeId)) {
                // A non-numeric parameter names no store, so it widens to all stores rather than leaving
                // an unrelated one applied — the same fallback the sub status parameter takes below.
                this.store = new Store();
            } else if (this.store?.id != storeId) {
                // Applied id-first so the table filters on this render; the name follows once fetched.
                this.store = new Store({ id: storeId } as Store);
                this.hydrateStoreName(storeId);
            }
        }

        // Present-but-empty means the linked cell holds prescriptions with no sub status, which the
        // filter cannot express, so it clears rather than narrowing to an unrelated sub status. A
        // repeated or non-numeric parameter parses as NaN and is treated the same way.
        if (hasSubStatus) {
            const parsed = Number(query.subStatus);
            this.subStatus = query.subStatus && !isNaN(parsed) ? parsed : null;
        }

        this.$nextTick(() => { this.restoringFilters = false; });
    }

    // A link carries only the store's id, but the Store filter renders its own label off the model, so an
    // id alone shows the empty placeholder — the page reads as unfiltered while it is in fact narrowed to
    // one store. Restored filters do not need this: they are saved with the name already on them.
    private async hydrateStoreName(storeId: number) {
        let store: Store;
        try {
            store = new Store((await Axios.get(`/Store/${storeId}`)).data);
        } catch {
            // The id alone still filters correctly, so leave it applied and let the label stay empty.
            return;
        }

        // A second link, or the user picking a store while this was in flight, owns the filter now.
        if (this.store?.id !== storeId) return;

        // Naming the store the link already selected is not a filter the user chose, so it must not
        // persist any more than the link itself did. setupFromQuery's suppression has lapsed by now —
        // it only spans the synchronous pass — so this round trip re-arms it for its own assignment.
        this.restoringFilters = true;
        this.store = store;
        this.$nextTick(() => { this.restoringFilters = false; });
    }

    @Watch('$route.query')
    queryChanged() {
        this.setupFromQuery();
    }

    @Watch("pageSize")
    onPageSizeChanged() {
        savePageSizePreference(this, 'pageSize_OnHoldQueue', this.pageSize);
    }

    private restoreFilters() {
        const saved = getUserSettingParsed<any>('onHoldQueue_filters', null);
        if (!saved) return;
        this.restoringFilters = true;
        if (saved.store?.id) this.store = new Store(saved.store);
        if (Array.isArray(saved.programIds)) {
            this.selectedProgramIds = saved.programIds;
        } else if (saved.program?.id) {
            // Carry forward a selection saved before the Program filter became multi-select.
            // TODO: Remove this branch once clients have had a release to migrate.
            this.selectedProgramIds = [saved.program.id];
        }
        if (Array.isArray(saved.drugs)) {
            this.selectedDrugs = saved.drugs.filter((d: any) => d?.key).map((d: any) => ({
                key: String(d.key),
                name: d.name ?? String(d.key),
            }));
        }
        if (saved.subStatus !== undefined) this.subStatus = saved.subStatus;
        this.$nextTick(() => { this.restoringFilters = false; });
    }

    /**
     * The persisted payload, as a computed so one watcher covers every filter that belongs in it.
     * Watching the fields individually fires the handler once per changed field per flush; the
     * date range is deliberately absent, and watching it would save a byte-identical payload.
     */
    private get persistedFilters(): string {
        return JSON.stringify({
            store: this.store?.id ? { id: this.store.id, name: this.store.name } : null,
            programIds: this.selectedProgramIds,
            drugs: this.selectedDrugs,
            subStatus: this.subStatus,
        });
    }

    /** Everything the drug option list is scoped by -- the filters above, minus the drugs. */
    private get drugScope(): string {
        return JSON.stringify([
            this.store?.id ?? null,
            this.subStatus,
            this.selectedProgramIds,
            this.dateFilter.startDate,
            this.dateFilter.endDate,
        ]);
    }

    private get allFields() {
        return [
            // Leads the grid so urgency reads at a glance, matching the Prior Authorization queue.
            // Sorts server-side: key "isUrgent" -> SortBy "IsUrgent". No sortByFormatted here - the
            // Prior Auth queue needs it because it sorts client-side off the formatted text, whereas
            // this grid is no-local-sorting and the stored procedure orders by the underlying bit.
            { key: "isUrgent", label: "Urgent", sortable: true },
            { key: "productNameShort", label: "Drug", sortable: true },
            // Prescription.NDC11 serializes as ndC11 (System.Text.Json camelCases only the first letter of an all-caps run); read that explicitly.
            { key: "ndc11", label: "NDC", sortable: true, formatter: (_v: unknown, _k: unknown, item: any) => item?.ndC11 ?? "" },
            { key: "patient", label: "Patient", formatter: this.patientFormatter, sortable: true },
            { key: "prescriberName", label: "Doctor", sortable: true },
            { key: "storeName", label: "Store", sortable: true },
            { key: "rxId", label: "Rx Id", sortable: true },
            { key: "quantity", label: "Quantity" },
            { key: "daySupply", label: "Day Supply" },
            { key: "dispensed", label: "Dispensed" },
            { key: "subStatus", label: "Hold Status", formatter: this.subStatusLabel, sortable: true },
            { key: "nextTouchDate", label: "Next Touch Date", formatter: this.localDateTimeFormatter, sortable: true },
            // Prescriptions.Updated is the UTC "last saved" stamp; render local date and time so it reads
            // against Next Touch Date. Sorts server-side: key "updated" -> SortBy "Updated".
            { key: "updated", label: "Updated", formatter: this.localDateTimeFormatter, sortable: true },
            { key: "fillDate", label: "Fill Date", formatter: (d: Date) => d ? moment.utc(d).format("L") : "", sortable: true },
            { key: "writtenDate", label: "Written Date", formatter: (d: Date) => d ? moment.utc(d).format("L") : "", sortable: true },
            { key: "programName", label: "Program", sortable: true },
            { key: "memo", label: "Memo" },
            { key: "directions", label: "Directions" },
            { key: "patientPay", label: "Patient Pay", formatter: (v: number | null) => v != null ? TextUtils.currencyFormatter(v) : "" },
            // Sorts server-side: key "primaryPlan" -> SortBy "PrimaryPlan". Rendered via #cell(primaryPlan) for the muted PBM line.
            { key: "primaryPlan", label: "Primary Plan", sortable: true },
            { key: "lastShippedDate", label: "Last Shipped", formatter: this.lastShippedFormatter, sortable: true },
            { key: "actions", label: "" },
        ];
    }

    get visibleFields() {
        return this.allFields.filter(f => f.key === "actions" || this.columnVisibility[f.key] !== false);
    }

    get tableFilter() {
        const filter: any = {
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
            fromDate: this.dateFilter.startDate,
            toDate: this.dateFilter.endDate,
            sortBy: this.sortBy ? this.sortBy.charAt(0).toUpperCase() + this.sortBy.slice(1) : this.sortBy,
            sortDirection: this.sortDesc ? "D" : "A"
        };

        // Only include optional parameters if they have valid values
        if (this.store?.id) {
            filter.storeId = this.store.id;
        }
        if (this.subStatus !== null && this.subStatus !== undefined) {
            filter.subStatus = this.subStatus;
        }
        // Sent as CSV rather than arrays so axios doesn't serialise them as programIds[]=1&...
        if (this.selectedProgramIds.length) {
            filter.programIds = this.selectedProgramIds.join(',');
        }
        if (this.selectedDrugs.length) {
            filter.drugs = this.selectedDrugKeys.join(',');
        }

        return filter;
    }

    @Watch("persistedFilters")
    onFilterChanged() {
        this.pageNumber = 1;
        if (!this.restoringFilters) saveUserSetting('onHoldQueue_filters', this.persistedFilters);
    }

    @Watch("drugScope")
    onDrugScopeChanged() {
        this.pageNumber = 1;
        // The set of drugs on hold depends on every other filter, so re-scope the options.
        // Skipped during restore because mounted() issues the initial load itself.
        if (!this.restoringFilters) this.loadDrugCounts();
    }

    @Watch("sortBy")
    @Watch("sortDesc")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    patientFormatter(pt: Patient) {
        const pat = Object.assign(new Patient(), pt);
        return pat.displayNamesForPerson();
    }

    lastShippedFormatter(value: Date | string | null, _key: string, item: any): string {
        return TextUtils.lastShippedFormatter(value, item?.lastShippedIsEquivalent);
    }

    localDateTimeFormatter(d: Date): string {
        return d ? moment.utc(d).local().format("L LT") : "";
    }

    async onHoldTableProvider(ctx: BvTableCtxObject) {
        const filter = ctx.filter as any;
        const config: AxiosRequestConfig = {
            params: filter,
        };
        this.loading = true;
        try {
            const resp = await Axios.get<any>(ctx.apiUrl!, config);
            this.totalItemCount = resp.data.count;
            return resp.data.items;
        } catch (err) {
            console.warn(err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }

        return [];
    }

    selectRx(item: Prescription, index?: number, event?: any) {
        const route: any = this.getPrescriptionDetailsRoute(item);
        this.$router.push(route);
    }

    getPrescriptionDetailsRoute(rxRow: Prescription) {
        return {
            name: 'PrescriptionDetails',
            params: {
                storeID: rxRow.storeID,
                rxNumber: rxRow.rxNumber,
                rfNumber: (rxRow.rfNumber || 0)
            }
        };
    }

    private onHoldSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];

    get activeOnHoldSubStatuses() {
        return this.onHoldSubStatuses.filter(ss => ss.active);
    }

    async getOnHoldSubStatuses() {
        try {
            const resp = await this.$http.get("/api/Configuration");
            this.onHoldSubStatuses = resp.data.onHoldSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active
            }));
        } catch {
            //ignore
        }
    }

    subStatusLabel(id: number) {
        return this.onHoldSubStatuses.find(ss => ss.value == id)?.label;
    }

    loadColumnPreferences() {
        const savedPreferences = getUserSetting('onHoldQueueColumnPreferences');
        if (savedPreferences) {
            try {
                const parsed = typeof savedPreferences === 'string' ? JSON.parse(savedPreferences) : savedPreferences;
                this.columnVisibility = { ...DEFAULT_COLUMN_VISIBILITY, ...parsed };
            } catch (err) {
                console.error('Failed to load column preferences:', err);
            }
        }
    }

    saveColumnPreferences() {
        try {
            saveUserSetting('onHoldQueueColumnPreferences', JSON.stringify(this.columnVisibility));
            this.$notification(NotificationOptions.notificationOptionsPreset(
                'Column preferences saved successfully',
                NotificationOptions.NotificationTypes.success
            ));
        } catch (err) {
            console.error('Failed to save column preferences:', err);
            this.$notification(NotificationOptions.error('Failed to save column preferences'));
        }
    }
}
</script>

<style scoped lang="scss">
.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
}

/* Matches the Prior Authorization queue's urgent badge so the two queues read the same. */
.urgent-highlight {
    animation: urgent-pulse 1.5s ease-in-out infinite;
}

@keyframes urgent-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
}

/* Sized to the pager bar's page-size select it sits beside, rather than the taller default button. */
.customize-columns {
    height: 25px;
    padding: 0 8px;
    font-size: 0.75rem;
    line-height: 1;
}
</style>
